import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, Checkbox, CircularProgress, Typography } from "@mui/material";
import GridIndex from "../../../components/GridIndex";
import axios from "../../../services/Api";
import useAlert from "../../../hooks/useAlert";
import useBreadcrumbs from "../../../hooks/useBreadcrumbs";
import {
  extractDailyPlannerContent,
  extractStudents,
  hasDailyPlannerTaskStarted,
} from "./dailyPlannerUtils";

const userID = JSON.parse(sessionStorage.getItem("AcharyaErpUser"))?.userId;

const isMarked = (status) =>
  status === true || status === false || status === 1 || status === 0;

const toPresent = (status) =>
  status === false || status === 0 ? "A" : "P";

const StudentActivityAttendance = () => {
  const { plannerId } = useParams();
  const navigate = useNavigate();
  const setCrumbs = useBreadcrumbs();
  const { setAlertMessage, setAlertOpen } = useAlert();
  const numericPlannerId = Number(plannerId);

  const [plannerItem, setPlannerItem] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectAll, setSelectAll] = useState(true);
  const [alreadyTaken, setAlreadyTaken] = useState(false);

  const isViewOnly = alreadyTaken;

  useEffect(() => {
    if (!numericPlannerId) {
      navigate("/daily-planner", { replace: true });
    }
  }, [numericPlannerId, navigate]);

  useEffect(() => {
    setCrumbs([
      { name: "Daily Planner", link: "/daily-planner" },
      { name: isViewOnly ? "View Attendance" : "Mark Attendance" },
    ]);
  }, [isViewOnly, setCrumbs]);

  useEffect(() => {
    const load = async () => {
      if (!userID || !numericPlannerId) return;

      setLoading(true);
      try {
        const plannerResponse = await axios.get(
          `/api/fetchAllDailyPlannerBasedOnUser?page=0&page_size=1000&sort=created_date&userId=${userID}`
        );
        const content = extractDailyPlannerContent(plannerResponse.data);
        const item = content.find((row) => Number(row.id) === numericPlannerId);

        if (!item || item.student_status !== true) {
          setAlertMessage({
            severity: "error",
            message: "Attendance is not available for this task",
          });
          setAlertOpen(true);
          navigate("/daily-planner", { replace: true });
          return;
        }

        setPlannerItem(item);

        const taken = item.attendance_status === true || item.attendance_status === 1;

        if (!taken && item.is_approved !== true) {
          setAlertMessage({
            severity: "error",
            message: "Attendance can be marked only after the task is approved",
          });
          setAlertOpen(true);
          navigate("/daily-planner", { replace: true });
          return;
        }

        if (!taken && !hasDailyPlannerTaskStarted(item)) {
          setAlertMessage({
            severity: "error",
            message: "Attendance can be marked after the task start time",
          });
          setAlertOpen(true);
          navigate("/daily-planner", { replace: true });
          return;
        }

        const studentsResponse = await axios.get(
          `/api/getActiveStudentsDetailsByDailyPlannerId/${numericPlannerId}`
        );
        const students = extractStudents(studentsResponse.data);
        const isTaken =
          taken ||
          students.some((student) => isMarked(student.attendance_status));

        setAlreadyTaken(isTaken);
        setRows(
          students.map((student, index) => {
            const present = isTaken
              ? toPresent(student.attendance_status)
              : "P";

            return {
              id: student.student_id ?? student.id ?? index,
              sl_no: index + 1,
              student_id: student.student_id ?? student.id,
              student_name: student.student_name || "Unknown",
              auid: student.auid ?? "",
              usn: student.usn ?? "",
              current_sem: student.current_sem,
              current_year: student.current_year,
              attendance_status: student.attendance_status,
              selected: present === "P",
              present,
            };
          })
        );
        setSelectAll(!isTaken);
      } catch (err) {
        console.error(err);
        setRows([]);
        setAlertMessage({
          severity: "error",
          message:
            err.response?.data?.message ||
            "Failed to fetch students for attendance",
        });
        setAlertOpen(true);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [numericPlannerId]);

  const toggleSelectAll = () => {
    if (isViewOnly) return;

    const next = !selectAll;
    setSelectAll(next);
    setRows((prev) =>
      prev.map((row) => ({
        ...row,
        selected: next,
        present: next ? "P" : "A",
      }))
    );
  };

  const handleSelect = (params) => {
    if (isViewOnly) return;

    setRows((prev) => {
      const nextRows = prev.map((row) => {
        if (row.student_id !== params.row.student_id) return row;

        const nextSelected = !row.selected;
        return {
          ...row,
          selected: nextSelected,
          present: nextSelected ? "P" : "A",
        };
      });

      setSelectAll(nextRows.every((row) => row.selected));
      return nextRows;
    });
  };

  const handleSubmit = async () => {
    if (isViewOnly || !numericPlannerId || rows.length === 0) return;

    if (
      plannerItem?.student_status !== true ||
      plannerItem?.is_approved !== true
    ) {
      setAlertMessage({
        severity: "error",
        message: "Attendance can be marked only after the task is approved",
      });
      setAlertOpen(true);
      return;
    }

    if (!hasDailyPlannerTaskStarted(plannerItem)) {
      setAlertMessage({
        severity: "error",
        message: "Attendance can be marked after the task start time",
      });
      setAlertOpen(true);
      return;
    }

    setSubmitting(true);
    try {
      const payload = rows.map((row) => ({
        student_id: row.student_id,
        attendance_status: row.present === "P",
      }));

      await axios.post(
        `/api/saveStudentAttendanceByDailyPlannerId/${numericPlannerId}`,
        payload
      );

      setAlertMessage({
        severity: "success",
        message: "Attendance submitted successfully",
      });
      setAlertOpen(true);
      navigate("/daily-planner", {
        state: { attendanceTakenId: numericPlannerId },
      });
    } catch (err) {
      console.error(err);
      setAlertMessage({
        severity: "error",
        message:
          err.response?.data?.message || "Failed to submit attendance",
      });
      setAlertOpen(true);
    } finally {
      setSubmitting(false);
    }
  };

  const columns = useMemo(
    () => [
      ...(!isViewOnly
        ? [
            {
              field: "selected",
              headerName: "",
              sortable: false,
              filterable: false,
              disableColumnMenu: true,
              width: 70,
              align: "center",
              headerAlign: "center",
              renderHeader: () => (
                <Checkbox
                  sx={{ padding: 0 }}
                  checked={selectAll}
                  indeterminate={!selectAll && rows.some((row) => row.selected)}
                  onChange={toggleSelectAll}
                />
              ),
              renderCell: (params) => (
                <Checkbox
                  sx={{ padding: 0 }}
                  checked={params.row.selected}
                  onChange={() => handleSelect(params)}
                />
              ),
            },
          ]
        : []),
      {
        field: "sl_no",
        headerName: "S.No.",
        flex: 0.4,
        sortable: false,
      },
      { field: "auid", headerName: "AUID", flex: 1 },
      { field: "student_name", headerName: "Student Name", flex: 1.2 },
      {
        field: "usn",
        headerName: "USN",
        flex: 1,
        valueGetter: (value, row) => row.usn || "-",
      },
      {
        field: "current_year",
        headerName: "Current Year",
        flex: 0.7,
        valueGetter: (value, row) => row.current_year ?? "-",
      },
      {
        field: "current_sem",
        headerName: "Current Sem",
        flex: 0.7,
        valueGetter: (value, row) => row.current_sem ?? "-",
      },
      {
        field: "Present",
        headerName: "Attendance",
        flex: 0.8,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <Box
            sx={{
              width: 36,
              height: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 0.5,
              fontWeight: 600,
              bgcolor:
                params.row.present === "P" ? "#a5d6a7" : "#ef9a9a",
            }}
          >
            {params.row.present}
          </Box>
        ),
      },
    ],
    [isViewOnly, selectAll, rows]
  );

  return (
    <Box sx={{ position: "relative", mt: 2 }}>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      ) : rows.length === 0 ? (
        <Typography sx={{ py: 3, textAlign: "center" }} color="text.secondary">
          No students are assigned to this task.
        </Typography>
      ) : (
        <>
          {/* {isViewOnly && (
            <Typography sx={{ mb: 1 }} color="text.secondary">
              Attendance is already taken for this task.
            </Typography>
          )} */}
          <GridIndex rows={rows} columns={columns} />
          {!isViewOnly && (
            <Button
              variant="contained"
              sx={{
                borderRadius: 2,
                backgroundColor: "green",
                color: "white",
                mt: 2,
                float: "right",
              }}
              onClick={handleSubmit}
              disabled={submitting || rows.length === 0}
            >
              {submitting ? "SUBMITTING..." : "SUBMIT"}
            </Button>
          )}
        </>
      )}
    </Box>
  );
};

export default StudentActivityAttendance;
